import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

function submitButtonText(loading, mode) {
  if (loading) return "Please wait…";
  return mode === "login" ? "Sign in" : "Create account";
}

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate(location.state?.from?.pathname || "/", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      <div className="hidden lg:flex w-[45%] flex-col justify-between p-10 text-white" style={{ background: "linear-gradient(160deg, #1B2A6B 0%, #101B45 100%)" }}>
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-lg p-1.5">
            <img src="/logo.png" alt="PT Bintang Wahyu Nuswantoro" className="h-10 w-auto" />
          </div>
        </div>
        <div>
          <div className="h-1 w-16 bg-[#E31E24] rounded-full mb-6" />
          <h1 className="font-heading text-3xl font-extrabold leading-tight mb-3">Plan. Track. Deliver.</h1>
          <p className="text-white/70 text-sm max-w-sm leading-relaxed">
            The internal project &amp; team management hub for PT. Bintang Wahyu Nuswantoro. Organize spaces, projects, tasks and subtasks in one place.
          </p>
        </div>
        <p className="text-white/40 text-xs">© 2026 PT. Bintang Wahyu Nuswantoro</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex justify-center mb-6">
            <img src="/logo.png" alt="BWN" className="h-12 w-auto" />
          </div>
          <div className="flex gap-1 p-1 bg-muted rounded-lg mb-6" data-testid="auth-mode-toggle">
            <button data-testid="login-tab" onClick={() => { setMode("login"); setError(""); }}
              className={`flex-1 h-8 rounded-md text-sm font-medium transition-colors ${mode === "login" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}>
              Sign in
            </button>
            <button data-testid="register-tab" onClick={() => { setMode("register"); setError(""); }}
              className={`flex-1 h-8 rounded-md text-sm font-medium transition-colors ${mode === "register" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}>
              Create account
            </button>
          </div>

          <h2 className="font-heading text-xl font-bold mb-1">{mode === "login" ? "Welcome back" : "Join the workspace"}</h2>
          <p className="text-sm text-muted-foreground mb-6">{mode === "login" ? "Sign in to your account to continue." : "Register to access the team workspace."}</p>

          <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
            {mode === "register" && (
              <input data-testid="register-name-input" type="text" required placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)}
                className="w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            )}
            <input data-testid="email-input" type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            <input data-testid="password-input" type="password" required minLength={6} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            {error && <p data-testid="auth-error" className="text-sm text-[#E31E24] font-medium">{error}</p>}
            <button data-testid="auth-submit-button" type="submit" disabled={loading}
              className="w-full h-9 rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white text-sm font-semibold transition-colors disabled:opacity-60">
              {submitButtonText(loading, mode)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
